import type { PaymentPlanInstallmentRow } from '@/data/payments';
import type { BadgeProps } from '@/components/ui/Badge';

// Shared, presentation-only helpers for the payment-plan surfaces (admin family
// detail + parent read-only view). Kept framework-light (no JSX) so both
// surfaces derive installment status, progress, and retry detail the same way.

type Tone = NonNullable<BadgeProps['tone']>;

/** Map an installment status to a Badge tone. Unknown statuses fall back to neutral. */
export function installmentTone(status: string): Tone {
  switch (status) {
    case 'paid':
      return 'success';
    case 'charged':
      return 'info';
    case 'scheduled':
      return 'neutral';
    case 'failed':
      return 'danger';
    case 'skipped':
      return 'warn';
    default:
      return 'neutral';
  }
}

/** Map a plan status to a Badge tone. */
export function planTone(status: string): Tone {
  switch (status) {
    case 'active':
      return 'info';
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'warn';
    case 'draft':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export interface PlanProgress {
  /** Installments in a terminal "money in" state (paid). */
  paidCount: number;
  /** Total installments on the plan. */
  totalCount: number;
  /** Cents collected (sum of paid installments). */
  paidCents: number;
  /** Cents across all installments. */
  totalCents: number;
}

/**
 * Derive paid / total progress for a plan from its installments. Pure and
 * allocation-stable inputs in, plain object out — callers must `useMemo` the
 * call site (react-hooks/exhaustive-deps is an ERROR in this repo).
 */
export function planProgress(installments: readonly PaymentPlanInstallmentRow[]): PlanProgress {
  let paidCount = 0;
  let paidCents = 0;
  let totalCents = 0;
  for (const inst of installments) {
    totalCents += inst.amount_cents;
    if (inst.status === 'paid') {
      paidCount += 1;
      paidCents += inst.amount_cents;
    }
  }
  return { paidCount, totalCount: installments.length, paidCents, totalCents };
}

/**
 * The generated `payment_plan_installments` Row type only guarantees the base
 * columns. The DB may also carry retry bookkeeping (attempts / last_error /
 * next_attempt_at). Read those defensively so a failed installment can show why
 * it failed without the UI assuming columns that the current generated type
 * does not declare.
 */
export interface InstallmentRetryDetail {
  attempts: number | null;
  lastError: string | null;
  nextAttemptAt: string | null;
}

export function installmentRetryDetail(
  installment: PaymentPlanInstallmentRow,
): InstallmentRetryDetail {
  const row = installment as Record<string, unknown>;
  const attempts = typeof row.attempts === 'number' ? row.attempts : null;
  const lastError = typeof row.last_error === 'string' ? row.last_error : null;
  const nextAttemptAt = typeof row.next_attempt_at === 'string' ? row.next_attempt_at : null;
  return { attempts, lastError, nextAttemptAt };
}

/** First scheduled installment's due date (the "next due" line), or null. */
export function nextDueDate(installments: readonly PaymentPlanInstallmentRow[]): string | null {
  // Installments arrive ordered by due_date ascending from the data layer.
  for (const inst of installments) {
    if (inst.status === 'scheduled' || inst.status === 'failed') return inst.due_date;
  }
  return null;
}
